// src/pages/ContactPage.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%);
    padding: 2rem 0;
    padding-top: 150px;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const PageHeader = styled.div`
    text-align: center;
    margin-bottom: 3rem;
`;

const PageTitle = styled(motion.h1)`
    font-size: 3rem;
    font-weight: 800;
    color: #000000;
    line-height: 1.1;
    margin-bottom: 2.5rem;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const PageSubtitle = styled(motion.p)`
    font-size: 1.2rem;
    color: #4a5568;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

// Contact Priority Notice
const ContactPriorityNotice = styled(motion.div)`
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
    border: 1px solid rgba(59, 130, 246, 0.3);
    color: #1e40af;
    padding: 1.5rem;
    border-radius: 12px;
    margin: 2rem auto;
    max-width: 800px;
    text-align: center;

    .notice-title {
        font-weight: 700;
        font-size: 1.1rem;
        margin-bottom: 0.5rem;
        color: #1e3a8a;
    }

    .notice-text {
        font-size: 1rem;
        line-height: 1.5;
        margin: 0;
    }

    @media (max-width: 768px) {
        padding: 1.25rem;
        margin: 1.5rem auto;
    }
`;

// New Full Width Map Section
const MapSection = styled.section`
    margin: 3rem 0;
`;

const MapHeader = styled(motion.h2)`
    font-size: 2rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 1.5rem;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 1.75rem;
    }
`;

const MapDescription = styled(motion.p)`
    font-size: 1.1rem;
    color: #4a5568;
    line-height: 1.6;
    text-align: center;
    max-width: 800px;
    margin: 0 auto 2rem;
`;

const MapContainer = styled(motion.div)`
    width: 100%;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.1);
`;

const MapIframe = styled.iframe`
    width: 100%;
    height: 400px;
    border: none;

    @media (max-width: 768px) {
        height: 300px;
    }
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    margin-top: 3rem;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
`;

const ContentSection = styled(motion.div)`
    h2 {
        font-size: 1.8rem;
        font-weight: 700;
        color: #000000;
        margin-bottom: 2rem;
        position: relative;
        padding-bottom: 0.75rem;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 60px;
            height: 3px;
            background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
            border-radius: 2px;
        }

        @media (max-width: 768px) {
            font-size: 1.5rem;
        }
    }
`;

const AddressCard = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
`;

const LocationImage = styled(motion.img)`
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 12px;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const LocationInfo = styled.div`
    margin-bottom: 1rem;

    p {
        font-size: 1rem;
        line-height: 1.6;
        color: #4a5568;
        margin-bottom: 1.5rem;
    }
`;

const CompanyInfo = styled.div`
    background: rgba(153, 1, 71, 0.05);
    padding: 1.5rem;
    border-radius: 12px;
    border-left: 4px solid #990147;
    margin: 1.5rem 0;

    .company-name {
        font-weight: 700;
        color: #990147;
        margin-bottom: 0.5rem;
        font-size: 1.1rem;
    }

    .doctor-name {
        font-weight: 600;
        color: #000000;
        font-size: 1rem;
    }
`;

const FullAddress = styled.div`
    background: rgba(59, 130, 246, 0.05);
    padding: 1rem;
    border-radius: 8px;
    border-left: 3px solid #3b82f6;
    margin: 1rem 0;

    .address-title {
        font-weight: 600;
        color: #1e40af;
        margin-bottom: 0.5rem;
        font-size: 0.95rem;
    }

    .address {
        color: #1e3a8a;
        font-weight: 500;
        font-size: 0.95rem;
        line-height: 1.4;
    }
`;

const TransportInfo = styled.div`
    margin-top: 2rem;

    h3 {
        font-size: 1.2rem;
        font-weight: 600;
        color: #000000;
        margin-bottom: 1rem;
    }
`;

const ParkingInfo = styled.div`
    background: rgba(16, 185, 129, 0.1);
    padding: 1rem;
    border-radius: 8px;
    border-left: 3px solid #10b981;
    margin-bottom: 1.5rem;

    p {
        color: #065f46;
        font-weight: 500;
        margin: 0;
        font-size: 0.95rem;
    }
`;

const BusInfo = styled.div`
    background: rgba(59, 130, 246, 0.1);
    padding: 1rem;
    border-radius: 8px;
    border-left: 3px solid #3b82f6;

    p {
        color: #1e40af;
        font-weight: 500;
        margin: 0 0 0.5rem 0;
        font-size: 0.95rem;
    }

    .bus-numbers {
        font-weight: 700;
        font-size: 1.1rem;
        color: #1e3a8a;
    }
`;

const ContactCard = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
`;

const ContactItem = styled(motion.div)`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 2rem;

    .icon {
        width: 24px;
        height: 24px;
        color: #990147;
        flex-shrink: 0;
        margin-top: 0.2rem;
        font-size: 1.2rem;
    }

    .content {
        flex: 1;

        .label {
            font-weight: 600;
            color: #000000;
            margin-bottom: 0.5rem;
            display: block;
            font-size: 1.1rem;
        }

        .value {
            font-size: 1rem;
            line-height: 1.5;
            color: #4a5568;
        }

        a {
            color: #990147;
            text-decoration: none;
            font-weight: 600;
            transition: all 0.3s ease;

            &:hover {
                color: #cc3366;
                text-decoration: underline;
            }
        }
    }
`;

const HoursGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-top: 1rem;
`;

const DayRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: rgba(153, 1, 71, 0.05);
    border-radius: 8px;

    &:nth-child(even) {
        background: rgba(153, 1, 71, 0.02);
    }

    .day {
        font-weight: 600;
        color: #000000;
    }

    .hours {
        color: #4a5568;
        font-weight: 500;
    }

    &[data-closed='true'] .hours {
        color: #94a3b8;
        font-weight: 500;
    }
`;

const HoursNote = styled.div`
    margin-top: 0.75rem;
    padding: 0.75rem;
    font-size: 0.9rem;
    font-style: italic;
    line-height: 1.4;
    color: #4a5568;
    background: rgba(153, 1, 71, 0.05);
    border: 1px solid rgba(153, 1, 71, 0.2);
    border-radius: 8px;
`;

const ImportantNote = styled(motion.div)`
    background: linear-gradient(135deg, rgba(153, 1, 71, 0.95) 0%, rgba(204, 51, 102, 0.95) 100%);
    color: white;
    padding: 2rem;
    border-radius: 16px;
    margin-top: 3rem;
    box-shadow: 0 8px 30px rgba(153, 1, 71, 0.3);

    p {
        font-size: 1.1rem;
        line-height: 1.6;
        margin: 0;
        text-align: center;
        font-weight: 500;
    }
`;

const ContactPage: React.FC = () => {
    const workingHours = [
        { day: 'Pondelok', hours: 'Zatvorené', closed: true },
        { day: 'Utorok', hours: 'Zatvorené', closed: true },
        { day: 'Streda', hours: 'Zatvorené', closed: true },
        { day: 'Štvrtok', hours: 'Zatvorené', closed: true },
        { day: 'Piatok', hours: '8.00-16.00 hod.', closed: false }
    ];

    // Simple Google Maps embed URL without complex parameters
    const googleMapsUrl =
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2670.8672912950717!2d17.094839876659357!3d48.11687197125753!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476c899bfa27558f%3A0x84401a92a874f883!2sPan%C3%B3nska%20cesta%203604%2F17%2C%20851%2001%20Petr%C5%BEalka!5e0!3m2!1sen!2ssk!4v1731607800092!5m2!1sen!2ssk\n";


    return (
        <PageContainer>
            <Container>
                <PageHeader>
                    <PageTitle
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        Kontakt
                    </PageTitle>
                    <PageSubtitle
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        Radi vás uvítame v našej ambulancii. Pre viac informácií nás neváhajte kontaktovať.
                    </PageSubtitle>
                </PageHeader>

                {/* Contact Priority Notice */}
                <ContactPriorityNotice
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="notice-title">Kontaktná priorita</div>
                    <p className="notice-text">
                        Z dôvodu vysokého počtu klientov vás najprv žiadame o kontaktovanie e-mailom.
                        V prípade, že vám e-mailová komunikácia nevyhovuje, môžete nás kontaktovať telefonicky.
                    </p>
                </ContactPriorityNotice>

                {/* Full Width Map Section */}
                <MapSection>
                    <MapContainer
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        <MapIframe
                            src={googleMapsUrl}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Lokalita ambulancie - Panónska cesta 17, Petržalka"
                            allowFullScreen
                        />
                    </MapContainer>
                </MapSection>

                <ContentGrid>
                    {/* Left Column - Address Information */}
                    <ContentSection
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                    >
                        <h2>Adresa</h2>
                        <AddressCard>
                            <LocationImage
                                src="/adresa.png"
                                alt="Budova ambulancie - City University"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.6 }}
                            />
                            <LocationInfo>
                                <p>
                                    Ambulancia je umiestnená v budove City University (Vysoká škola manažmentu)
                                    na Panónskej ceste 17, na 3. poschodí, č. dverí 301. Prístup je bezbariérový.
                                </p>

                                <FullAddress>
                                    <div className="address-title">Plná adresa:</div>
                                    <div className="address">
                                        Panónska cesta 3604/17<br />
                                        851 01 Petržalka
                                    </div>
                                </FullAddress>

                                <CompanyInfo>
                                    <div className="company-name">SB Consulting Slovakia, s.r.o.</div>
                                    <div className="doctor-name">MUDr. Soňa Belanská</div>
                                </CompanyInfo>

                                <TransportInfo>
                                    <ParkingInfo>
                                        <p>Parkovanie pred budovou a v priľahlých uliciach je zdarma.</p>
                                    </ParkingInfo>

                                    <BusInfo>
                                        <p>MHD: zastávka „Ševčenkova" (je na znamenie)</p>
                                        <div className="bus-numbers">91, 191, 93, 94</div>
                                        <p style={{ color: '#1e40af', marginTop: '0.5rem', marginBottom: '0' }}>
                                            Zastávka je vzdialená asi 250 metrov od budovy.
                                        </p>
                                    </BusInfo>
                                </TransportInfo>
                            </LocationInfo>
                        </AddressCard>
                    </ContentSection>

                    {/* Right Column - Contact Information */}
                    <ContentSection
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                    >
                        <h2>Kontakt</h2>
                        <ContactCard>
                            <ContactItem
                                whileHover={{ x: 5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="icon">📧</div>
                                <div className="content">
                                    <span className="label">Email (prednostný kontakt)</span>
                                    <div className="value">
                                        <a href="mailto:sonabelanska@gmail.com">
                                            sonabelanska@gmail.com
                                        </a>
                                    </div>
                                </div>
                            </ContactItem>

                            <ContactItem
                                whileHover={{ x: 5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="icon">📞</div>
                                <div className="content">
                                    <span className="label">Telefón (sekundárny kontakt)</span>
                                    <div className="value">
                                        {/* Add your phone number here later */}
                                        +421 XXX XXX XXX
                                    </div>
                                </div>
                            </ContactItem>

                            <ContactItem
                                whileHover={{ x: 5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="icon">🕒</div>
                                <div className="content">
                                    <span className="label">Ordinačné hodiny</span>
                                    <HoursGrid>
                                        {workingHours.map((day, index) => (
                                            <DayRow key={index} data-closed={day.closed}>
                                                <span className="day">{day.day}</span>
                                                <span className="hours">{day.hours}</span>
                                            </DayRow>
                                        ))}
                                    </HoursGrid>
                                    <HoursNote>
                                        Posledný pacient je prijatý o 15.00 hod.
                                    </HoursNote>
                                </div>
                            </ContactItem>
                        </ContactCard>
                    </ContentSection>
                </ContentGrid>

                <ImportantNote
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                >
                    <p>
                        Konzultácie a vyšetrenia sú možné iba po predchádzajúcom objednaní.
                        Pre naplnenú kapacitu, ambulancia momentálne nových pacientov neprijíma.
                        Na stretnutie sa pacient objednáva vopred, po telefonickom dohovore, na konkrétny deň a hodinu.
                    </p>
                </ImportantNote>
            </Container>
        </PageContainer>
    );
};

export default ContactPage;